import type { ApiError, ApiErrorCode } from '@shared/types'

export class ApiFailure extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly field?: string,
    readonly status = 400,
    /** Seconds until a retry is worth making, from the `retry-after` header. */
    readonly retryAfter?: number,
  ) {
    super(message)
  }
}

function newIdempotencyKey(): string {
  return (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** reuse a key when retrying the same logical write */
  idempotencyKey?: string
  signal?: AbortSignal
}

export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method ?? 'GET'
  const headers: Record<string, string> = {}
  let body: string | undefined

  if (opts.body !== undefined) {
    headers['content-type'] = 'application/json'
    body = JSON.stringify(opts.body)
  }
  if (method !== 'GET') {
    headers['idempotency-key'] = opts.idempotencyKey ?? newIdempotencyKey()
  }

  const res = await fetch(path, { method, headers, body, credentials: 'same-origin', signal: opts.signal })

  if (res.status === 204) return undefined as T

  let payload: unknown
  const text = await res.text()
  try { payload = text ? JSON.parse(text) : undefined } catch { payload = undefined }

  if (!res.ok) {
    const e = payload as ApiError | undefined
    // The server knows exactly how long the caller has to wait; the screens
    // used to guess, and guessed wrong the moment a limit was retuned.
    const retryAfterHeader = Number(res.headers.get('retry-after'))
    throw new ApiFailure(
      e?.error?.code ?? 'server_error',
      e?.error?.message ?? 'Something went wrong.',
      e?.error?.field,
      res.status,
      Number.isFinite(retryAfterHeader) && retryAfterHeader > 0 ? retryAfterHeader : undefined,
    )
  }
  return payload as T
}

/**
 * A multipart POST, for the two places that carry a file.
 *
 * It deliberately does not go through `api()`: that helper sets
 * `content-type: application/json`, and a `FormData` body needs the browser to
 * set the header itself so it can append the multipart boundary. Setting it by
 * hand produces a request the Worker cannot parse and an error message that
 * points nowhere near the cause.
 */
export async function postForm<T>(path: string, form: FormData, key?: string): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'idempotency-key': key ?? newIdempotencyKey() },
    body: form,
    credentials: 'same-origin',
  })

  const text = await res.text()
  let payload: unknown
  try { payload = text ? JSON.parse(text) : undefined } catch { payload = undefined }

  if (!res.ok) {
    const e = payload as ApiError | undefined
    const retryAfterHeader = Number(res.headers.get('retry-after'))
    throw new ApiFailure(
      e?.error?.code ?? 'server_error',
      e?.error?.message ?? 'Something went wrong.',
      e?.error?.field,
      res.status,
      Number.isFinite(retryAfterHeader) && retryAfterHeader > 0 ? retryAfterHeader : undefined,
    )
  }
  return payload as T
}

export const GET = <T>(p: string, signal?: AbortSignal) => api<T>(p, { signal })
export const POST = <T>(p: string, body?: unknown, key?: string) => api<T>(p, { method: 'POST', body, idempotencyKey: key })
export const PUT = <T>(p: string, body?: unknown, key?: string) => api<T>(p, { method: 'PUT', body, idempotencyKey: key })
export const PATCH = <T>(p: string, body?: unknown, key?: string) => api<T>(p, { method: 'PATCH', body, idempotencyKey: key })
export const DEL = <T>(p: string, body?: unknown, key?: string) => api<T>(p, { method: 'DELETE', body, idempotencyKey: key })

// ── offline write queue (D103) ──────────────────────────────────────────────
//
// Writes attempted while offline are queued and replayed on reconnect. Every
// queued write carries a client-generated idempotency key, so a replay after a
// partially-succeeded request cannot duplicate.

const QUEUE_KEY = 'sfg.queue.v1'

export interface QueuedWrite {
  key: string
  path: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  at: number
  label: string
}

function loadQueue(): QueuedWrite[] {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') as QueuedWrite[] } catch { return [] }
}
function saveQueue(q: QueuedWrite[]): void {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)) } catch { /* private mode */ }
}

export function queueSize(): number { return loadQueue().length }

export function enqueue(w: Omit<QueuedWrite, 'key' | 'at'>): QueuedWrite {
  const item: QueuedWrite = { ...w, key: newIdempotencyKey(), at: Date.now() }
  const q = loadQueue()
  q.push(item)
  saveQueue(q)
  return item
}

export async function flushQueue(): Promise<{ sent: number; failed: number }> {
  let q = loadQueue()
  if (!q.length) return { sent: 0, failed: 0 }
  let sent = 0, failed = 0
  const remaining: QueuedWrite[] = []
  for (const item of q) {
    try {
      await api(item.path, { method: item.method, body: item.body, idempotencyKey: item.key })
      sent++
    } catch (e) {
      // A 4xx means the server rejected it on the merits — drop it, don't spin.
      if (e instanceof ApiFailure && e.status >= 400 && e.status < 500 && e.code !== 'rate_limited') {
        failed++
      } else {
        remaining.push(item)
      }
    }
  }
  saveQueue(remaining)
  return { sent, failed }
}

/**
 * Perform a write, queueing it instead of failing when the browser is offline.
 * Returns null when queued.
 */
export async function writeOrQueue<T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body: unknown,
  label: string,
): Promise<T | null> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    enqueue({ path, method, body, label })
    return null
  }
  try {
    return await api<T>(path, { method, body })
  } catch (e) {
    if (e instanceof TypeError) { // network failure, not a server rejection
      enqueue({ path, method, body, label })
      return null
    }
    throw e
  }
}
